import type { ReactElement } from 'react'
import { Box, Chip, Stack } from '@mui/material'
import type { ChipProps } from '@mui/material'

export interface ToggleChipGroupItem {
  key: string
  label: string
  selected: boolean
  /** Muted number after the label, e.g. «Всё 9». */
  count?: number
  /** Leading icon, e.g. the trash chip of the request list. */
  icon?: ReactElement
  disabled?: boolean
  onToggle: () => void
}

type ToggleChipGroupPreset = 'neutral' | 'quickFilters' | 'contrast' | 'contrastCompact'
type ToggleChipGroupScenario = 'requestQuickFilters'

interface ToggleChipGroupProps {
  items: ToggleChipGroupItem[]
  preset?: ToggleChipGroupPreset
  scenario?: ToggleChipGroupScenario
  wrap?: boolean
  /** Raises the chips to a 44px touch target. */
  touchTarget?: boolean
  ariaLabel?: string
}

type ToggleChipSx = Record<string, string | number | Record<string, string>>

const CONTRAST_BASE_SX = {
  height: 30,
  borderRadius: '999px',
  fontSize: 13,
  fontWeight: 500,
  border: '1px solid',
  '& .MuiChip-label': { paddingLeft: '12px', paddingRight: '12px' },
  '&.Mui-focusVisible': { outline: '2px solid #1565c0', outlineOffset: '2px' }
} as const

const CONTRAST_SELECTED_SX = {
  bgcolor: '#15202b',
  borderColor: '#15202b',
  color: '#fff',
  '&:hover, &.Mui-focusVisible': { backgroundColor: '#15202b' }
} as const

const CONTRAST_UNSELECTED_SX = {
  bgcolor: '#fff',
  borderColor: '#cfd6e2',
  color: '#5b6572'
} as const

const TOGGLE_CHIP_GROUP_PRESETS = {
  neutral: {
    selectedColor: 'default',
    unselectedSx: {
      borderColor: 'divider',
      color: 'text.secondary'
    }
  },
  quickFilters: {
    selectedColor: 'primary',
    unselectedSx: {
      borderColor: 'divider',
      color: 'text.secondary'
    }
  },
  // Pill filters of the request event feed: dark fill when selected, white with a grey border otherwise.
  contrast: {
    selectedColor: 'default',
    baseSx: CONTRAST_BASE_SX,
    selectedSx: CONTRAST_SELECTED_SX,
    unselectedSx: CONTRAST_UNSELECTED_SX
  },
  // Share presets of the send-to-payment dialog: the contrast pills at 28px.
  contrastCompact: {
    selectedColor: 'default',
    baseSx: { ...CONTRAST_BASE_SX, height: 28, fontSize: 12.5, fontWeight: 600 },
    selectedSx: CONTRAST_SELECTED_SX,
    unselectedSx: CONTRAST_UNSELECTED_SX
  }
} as const satisfies Record<ToggleChipGroupPreset, {
  selectedColor: ChipProps['color']
  baseSx?: ToggleChipSx
  selectedSx?: ToggleChipSx
  unselectedSx: ToggleChipSx
}>

const EMPTY_SX: ToggleChipSx = {}

const TOGGLE_CHIP_GROUP_SCENARIOS = {
  requestQuickFilters: {
    preset: 'quickFilters'
  }
} as const satisfies Record<ToggleChipGroupScenario, {
  preset: ToggleChipGroupPreset
}>

export default function ToggleChipGroup({
  items,
  preset,
  scenario,
  wrap = true,
  touchTarget = false,
  ariaLabel
}: ToggleChipGroupProps): ReactElement {
  const resolvedScenario = scenario ? TOGGLE_CHIP_GROUP_SCENARIOS[scenario] : null
  const resolvedPreset: {
    selectedColor: ChipProps['color']
    baseSx?: ToggleChipSx
    selectedSx?: ToggleChipSx
    unselectedSx: ToggleChipSx
  } = TOGGLE_CHIP_GROUP_PRESETS[preset ?? resolvedScenario?.preset ?? 'neutral']

  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap={wrap ? 'wrap' : 'nowrap'}
      useFlexGap
      role={ariaLabel ? 'group' : undefined}
      aria-label={ariaLabel}
    >
      {items.map((item) => (
        <Chip
          key={item.key}
          icon={item.icon}
          label={item.count === undefined ? item.label : (
            <>
              {item.label}{' '}
              <Box component="span" sx={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>{item.count}</Box>
            </>
          )}
          clickable
          disabled={item.disabled}
          onClick={item.onToggle}
          aria-pressed={item.selected}
          variant={item.selected ? 'filled' : 'outlined'}
          color={item.selected ? resolvedPreset.selectedColor : 'default'}
          sx={{
            flexShrink: 0,
            transition: 'all 0.15s ease',
            ...(resolvedPreset.baseSx ?? EMPTY_SX),
            ...(item.selected ? (resolvedPreset.selectedSx ?? EMPTY_SX) : resolvedPreset.unselectedSx),
            ...(touchTarget ? { height: 44 } : EMPTY_SX)
          }}
        />
      ))}
    </Stack>
  )
}
